import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  UserPlus, Pencil, Trash2, Star, Mail, Phone, Smartphone,
  X as XIcon, Briefcase, Tag, Save, Lock, MessageCircle,
} from "lucide-react";
import {
  listAnsprechpartner, addAnsprechpartner, updateAnsprechpartner,
  deleteAnsprechpartner, ROLLE_TAGS,
} from "../../lib/api";

// Farben pro Rolle-Tag — gut unterscheidbar
const ROLLE_COLORS = {
  "CEO/GF":         "bg-navy/10 text-navy border-navy/30",
  "Investment":     "bg-heiss/10 text-heiss border-heiss/30",
  "Asset Mgmt":     "bg-gold/10 text-gold border-gold/30",
  "Legal":          "bg-tot/10 text-tot border-tot/30",
  "Family Member":  "bg-reaktiv/10 text-reaktiv border-reaktiv/30",
  "Berater":        "bg-paper text-navy/70 border-navy/20",
};

const initials = (vor, nach) =>
  ((vor || "")[0] || "").toUpperCase() + ((nach || "")[0] || "").toUpperCase() || "?";

export default function AnsprechpartnerTab({ investorId, investorFirma, onChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingAp, setEditingAp] = useState(null);   // ap | "new" | null
  const [busyKey, setBusyKey] = useState(null);

  const load = useCallback(async () => {
    if (!investorId) return;
    setLoading(true);
    try {
      const r = await listAnsprechpartner(investorId);
      setItems(r.items || []);
    } catch {
      toast.error("Ansprechpartner konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (ap) => {
    if (!window.confirm(`Ansprechpartner „${ap.vorname} ${ap.nachname}" wirklich löschen?`)) return;
    setBusyKey(ap.id);
    try {
      await deleteAnsprechpartner(investorId, ap.id);
      toast.success(`${ap.vorname} ${ap.nachname} gelöscht`);
      await load();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setBusyKey(null);
    }
  };

  return (
    <div className="space-y-4" data-testid="drawer-ansprechpartner-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="pb-eyebrow">Ansprechpartner</div>
          <p className="text-xs font-sans text-navy/55 mt-0.5">
            <strong className="text-navy">{items.length}</strong> Kontakt{items.length === 1 ? "" : "e"} bei <strong className="text-navy">{investorFirma}</strong>
          </p>
        </div>
        <button
          onClick={() => setEditingAp("new")}
          data-testid="ap-add-btn"
          className="pb-btn-primary text-xs uppercase tracking-widish font-semibold min-h-[40px] gap-2"
        >
          <UserPlus size={13} /> Neuer Kontakt
        </button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-navy/55 font-sans text-sm">Lade…</div>
      ) : items.length === 0 ? (
        <div className="bg-paper border border-dashed border-navy/20 p-8 text-center">
          <UserPlus size={28} className="mx-auto text-navy/30 mb-2" />
          <p className="text-sm font-sans text-navy/65">Noch keine Ansprechpartner hinterlegt.</p>
          <p className="text-[11px] font-sans text-navy/45 mt-1">
            Klicke oben rechts auf „Neuer Kontakt", um einen Eintrag anzulegen.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="ap-cards">
          {items.map((ap) => (
            <ApCard
              key={ap.id}
              ap={ap}
              busy={busyKey === ap.id}
              onEdit={() => setEditingAp(ap)}
              onDelete={() => handleDelete(ap)}
            />
          ))}
        </div>
      )}

      {editingAp && (
        <ApModal
          investorId={investorId}
          ap={editingAp === "new" ? null : editingAp}
          onClose={() => setEditingAp(null)}
          onSaved={async () => {
            setEditingAp(null);
            await load();
            onChange?.();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// ApCard
// ============================================================================
function ApCard({ ap, busy, onEdit, onDelete }) {
  const [expandedNotiz, setExpandedNotiz] = useState(false);
  const nameLine = `${ap.titel ? ap.titel + " " : ""}${ap.vorname || ""} ${ap.nachname || ""}`.trim();

  return (
    <article
      data-testid={`ap-card-${ap.id}`}
      className={"bg-white border " + (ap.primaer ? "border-gold/60" : "border-navy/15") + " p-4 flex flex-col gap-3 min-w-0"}
    >
      {/* Header */}
      <header className="flex items-start gap-3 min-w-0">
        <div
          className={"shrink-0 size-10 grid place-items-center font-display font-bold text-sm tracking-wider " +
            (ap.primaer ? "bg-gold text-paper" : "bg-navy/10 text-navy")}
          aria-hidden
        >
          {initials(ap.vorname, ap.nachname)}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2 min-w-0">
            <h4 className="font-display font-semibold text-navy text-base tracking-tight min-w-0 truncate">
              {ap.anrede && <span className="text-navy/55 font-sans font-normal text-xs mr-1">{ap.anrede}</span>}
              {nameLine || "—"}
            </h4>
            {ap.primaer && <Star size={12} className="text-gold mt-1.5 shrink-0" fill="currentColor" />}
          </div>
          {ap.funktion && (
            <div className="text-[11px] font-sans text-navy/65 mt-0.5 flex items-center gap-1 min-w-0">
              <Briefcase size={10} className="shrink-0" />
              <span className="truncate">{ap.funktion}</span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button
            onClick={onEdit}
            disabled={busy}
            data-testid={`ap-edit-${ap.id}`}
            className="text-navy/55 hover:text-gold p-1"
            title="Bearbeiten"
          >
            <Pencil size={13} />
          </button>
          <button
            onClick={onDelete}
            disabled={busy}
            data-testid={`ap-delete-${ap.id}`}
            className="text-navy/55 hover:text-tot p-1"
            title="Löschen"
          >
            <Trash2 size={13} />
          </button>
        </div>
      </header>

      {/* Kontaktdaten */}
      <ul className="space-y-1 text-xs font-sans text-navy/80">
        {ap.email && (
          <li className="flex items-center gap-2 min-w-0">
            <Mail size={11} className="text-navy/45 shrink-0" />
            <a href={`mailto:${ap.email}`} className="hover:text-gold truncate min-w-0">{ap.email}</a>
          </li>
        )}
        {ap.telefon && (
          <li className="flex items-center gap-2 min-w-0">
            <Phone size={11} className="text-navy/45 shrink-0" />
            <a href={`tel:${ap.telefon}`} className="hover:text-gold truncate min-w-0">{ap.telefon}</a>
          </li>
        )}
        {ap.mobil && (
          <li className="flex items-center gap-2 min-w-0">
            <Smartphone size={11} className="text-navy/45 shrink-0" />
            <a href={`tel:${ap.mobil}`} className="hover:text-gold truncate min-w-0">{ap.mobil}</a>
          </li>
        )}
      </ul>

      {/* Rolle-Tags */}
      {ap.rolle_tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {ap.rolle_tags.map((t) => (
            <span
              key={t}
              className={"inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-widish border " + (ROLLE_COLORS[t] || "border-navy/20 text-navy/65 bg-paper")}
            >
              <Tag size={9} /> {t}
            </span>
          ))}
        </div>
      )}

      {/* Empfänger-Badges */}
      <div className="flex flex-wrap gap-2 text-[10px] uppercase tracking-widish font-sans text-navy/55">
        {ap.ist_nda_empfaenger && (
          <span title="Empfänger für NDA-Mails" className="inline-flex items-center gap-1 text-heiss">
            <Mail size={9} /> NDA
          </span>
        )}
        {ap.ist_datenraum_empfaenger && (
          <span title="Empfänger für Datenraum-Mails" className="inline-flex items-center gap-1 text-gold">
            <Mail size={9} /> Datenraum
          </span>
        )}
        {ap.ist_vertrags_empfaenger && (
          <span title="Empfänger für Vertrags-Mails" className="inline-flex items-center gap-1 text-tot">
            <Mail size={9} /> Vertrag
          </span>
        )}
      </div>

      {/* Notiz */}
      {ap.notiz && (
        <div className="border-t border-navy/10 pt-2 text-[11px] font-sans text-navy/65 flex items-start gap-1.5">
          <MessageCircle size={11} className="text-navy/45 mt-0.5 shrink-0" />
          <span className={"min-w-0 " + (expandedNotiz ? "" : "line-clamp-3")}>
            {ap.notiz}{" "}
            {ap.notiz.length > 100 && (
              <button
                onClick={() => setExpandedNotiz((v) => !v)}
                className="text-gold hover:underline ml-1"
              >
                {expandedNotiz ? "weniger" : "mehr"}
              </button>
            )}
          </span>
        </div>
      )}
    </article>
  );
}

// ============================================================================
// ApModal — Create + Edit
// ============================================================================
function ApModal({ investorId, ap, onClose, onSaved }) {
  const isEdit = !!ap;
  const [form, setForm] = useState({
    anrede: ap?.anrede || "",
    titel: ap?.titel || "",
    vorname: ap?.vorname || "",
    nachname: ap?.nachname || "",
    email: ap?.email || "",
    telefon: ap?.telefon || "",
    mobil: ap?.mobil || "",
    funktion: ap?.funktion || "",
    rolle_tags: ap?.rolle_tags || [],
    primaer: ap?.primaer || false,
    ist_nda_empfaenger: ap?.ist_nda_empfaenger || false,
    ist_datenraum_empfaenger: ap?.ist_datenraum_empfaenger || false,
    ist_vertrags_empfaenger: ap?.ist_vertrags_empfaenger || false,
    notiz: ap?.notiz || "",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTag = (t) =>
    set("rolle_tags", form.rolle_tags.includes(t) ? form.rolle_tags.filter((x) => x !== t) : [...form.rolle_tags, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.vorname.trim() || !form.nachname.trim()) {
      toast.error("Vorname und Nachname sind Pflicht.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await updateAnsprechpartner(investorId, ap.id, form);
        toast.success("Ansprechpartner aktualisiert");
      } else {
        await addAnsprechpartner(investorId, form);
        toast.success(`${form.vorname} ${form.nachname} angelegt`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-navy/40 z-[60]" />
      <div
        data-testid="ap-modal"
        className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(640px,95vw)] max-h-[90vh] overflow-y-auto bg-white border border-navy/15 z-[70] shadow-xl"
      >
        <header className="border-b border-navy/10 px-6 py-4 flex items-center justify-between">
          <div>
            <div className="pb-eyebrow">{isEdit ? "Bearbeiten" : "Neuer Kontakt"}</div>
            <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">
              {isEdit ? `${ap.vorname} ${ap.nachname}` : "Ansprechpartner anlegen"}
            </h3>
          </div>
          <button onClick={onClose} className="text-navy/55 hover:text-navy p-1" aria-label="Schließen">
            <XIcon size={18} />
          </button>
        </header>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          {/* Anrede, Titel, Vor-, Nachname */}
          <div className="grid grid-cols-2 sm:grid-cols-12 gap-3">
            <div className="col-span-1 sm:col-span-3">
              <label className="pb-label">Anrede</label>
              <select data-testid="ap-form-anrede" value={form.anrede} onChange={(e) => set("anrede", e.target.value)} className="pb-input--full">
                <option value="">—</option>
                <option>Herr</option>
                <option>Frau</option>
                <option>Divers</option>
              </select>
            </div>
            <div className="col-span-1 sm:col-span-3">
              <label className="pb-label">Titel</label>
              <input data-testid="ap-form-titel" value={form.titel} onChange={(e) => set("titel", e.target.value)} placeholder="Dr." className="pb-input--full" />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <label className="pb-label">Vorname <span className="text-tot">*</span></label>
              <input data-testid="ap-form-vorname" value={form.vorname} onChange={(e) => set("vorname", e.target.value)} required className="pb-input--full" />
            </div>
            <div className="col-span-2 sm:col-span-3">
              <label className="pb-label">Nachname <span className="text-tot">*</span></label>
              <input data-testid="ap-form-nachname" value={form.nachname} onChange={(e) => set("nachname", e.target.value)} required className="pb-input--full" />
            </div>
          </div>

          {/* Funktion */}
          <div>
            <label className="pb-label">Funktion</label>
            <input data-testid="ap-form-funktion" value={form.funktion} onChange={(e) => set("funktion", e.target.value)} placeholder="z. B. Head of Acquisitions Europe" className="pb-input--full" />
          </div>

          {/* Email + Telefon + Mobil */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="pb-label">E-Mail</label>
              <input data-testid="ap-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="name@firma.com" className="pb-input--full" />
            </div>
            <div>
              <label className="pb-label">Telefon</label>
              <input data-testid="ap-form-telefon" value={form.telefon} onChange={(e) => set("telefon", e.target.value)} placeholder="+49 …" className="pb-input--full" />
            </div>
            <div>
              <label className="pb-label">Mobil</label>
              <input data-testid="ap-form-mobil" value={form.mobil} onChange={(e) => set("mobil", e.target.value)} placeholder="+49 …" className="pb-input--full" />
            </div>
          </div>

          {/* Rolle-Tags */}
          <div>
            <label className="pb-label">Rolle-Tags (Multi-Select)</label>
            <div className="flex flex-wrap gap-2 mt-1">
              {ROLLE_TAGS.map((t) => {
                const on = form.rolle_tags.includes(t);
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleTag(t)}
                    data-testid={`ap-form-tag-${t}`}
                    className={
                      "inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-widish border font-sans transition-colors " +
                      (on ? (ROLLE_COLORS[t] || "border-navy bg-navy text-paper") : "border-navy/20 text-navy/55 hover:border-navy/40")
                    }
                  >
                    {on && <Tag size={9} />} {t}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Flags */}
          <div className="border-t border-b border-navy/10 py-4 space-y-2.5">
            <FlagToggle
              testid="ap-form-primaer"
              checked={form.primaer}
              onChange={(v) => set("primaer", v)}
              icon={<Star size={11} fill={form.primaer ? "currentColor" : "none"} />}
              label="Primärkontakt"
              hint="Standardempfänger für direkte Ansprache (nur 1 pro Investor)"
              accent="text-gold"
            />
            <FlagToggle
              testid="ap-form-nda"
              checked={form.ist_nda_empfaenger}
              onChange={(v) => set("ist_nda_empfaenger", v)}
              icon={<Mail size={11} />}
              label="NDA-Empfänger"
              hint="Erhält NDA-Anfragen und -Erinnerungen"
              accent="text-heiss"
            />
            <FlagToggle
              testid="ap-form-dr"
              checked={form.ist_datenraum_empfaenger}
              onChange={(v) => set("ist_datenraum_empfaenger", v)}
              icon={<Mail size={11} />}
              label="Datenraum-Empfänger"
              hint="Erhält Datenraum-Einladungen mit Magic-Link"
              accent="text-gold"
            />
            <FlagToggle
              testid="ap-form-vertrag"
              checked={form.ist_vertrags_empfaenger}
              onChange={(v) => set("ist_vertrags_empfaenger", v)}
              icon={<Mail size={11} />}
              label="Vertrags-Empfänger"
              hint="Erhält SPA / Verträge zur Prüfung"
              accent="text-tot"
            />
          </div>

          {/* Notiz */}
          <div>
            <label className="pb-label">Notiz</label>
            <textarea
              data-testid="ap-form-notiz"
              value={form.notiz}
              onChange={(e) => set("notiz", e.target.value)}
              rows={3}
              placeholder="z. B. Spricht nur Englisch · Bayern-Sympathisant · letzte Mtg 04/2024"
              className="pb-input--full font-sans"
            />
          </div>

          {/* Aktionen */}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="pb-btn-ghost">Abbrechen</button>
            <button
              type="submit"
              data-testid="ap-form-save"
              disabled={saving}
              className="pb-btn-primary gap-2"
            >
              <Save size={13} /> {saving ? "Speichert…" : (isEdit ? "Speichern" : "Anlegen")}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function FlagToggle({ checked, onChange, icon, label, hint, accent, testid }) {
  return (
    <label className="flex items-start gap-3 cursor-pointer min-w-0">
      <input
        type="checkbox"
        data-testid={testid}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 size-4 accent-[#022750]"
      />
      <div className="min-w-0">
        <div className={"text-xs font-sans font-medium flex items-center gap-1.5 " + (checked ? accent : "text-navy/75")}>
          {icon} {label}
        </div>
        <div className="text-[11px] font-sans text-navy/50 mt-0.5">{hint}</div>
      </div>
    </label>
  );
}
